import { NewOrderSearchQueryVariables, OrderStatus, SearchedOrderWrapper, SortOrder } from 'API';
import Button from 'components/common/Button/Button';
import Input from 'components/common/Input/Input';
import MultiSelectSearchableDropdown from 'components/common/MultiselectSearchableDropdown/MultiselectSearchableDropdown';
import { getSupportedCaseStatuses } from 'configurations/CaseSearchConfiguration';
import { capitalize } from 'lodash';
import { useToast } from 'providers/ToastProvider';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { newOrderSearch } from 'shared/api/order.api';
import { CaseStatus } from 'shared/constants/constants';
import { CASE_STATUS_OPTIONS } from 'shared/constants/search.constants';
import { ToastNotificationType } from 'shared/enums';
import { AnalyticsEventName } from 'shared/enums/analytics';
import { convertSortableOrderToString, getSortableOrder } from 'shared/helpers/util.helper';
import { useLazyQueryFetcher } from 'shared/hooks/useLazyQueryFetcher';
import { DropdownOption } from 'shared/models';
import { AnalyticsService } from 'shared/services/analytics.service';
import { SearchTable, SearchTableProps } from './SearchTable/SearchTable';

const defaultSearchState: SearchTableProps['searchState'] = {
  caseNumber: '',
  patientFirstName: '',
  patientLastName: '',
  providerName: '',
  accountNumber: '',
  statuses: [],
};

/**
 * Renders a page listing search results for submitted search value
 * search results can be narrowed using various filters available like case status, case, account etc.,
 */
export const SearchDetailPage: FC = () => {
  const { fetcher: orderSearchFetcher, loading } = useLazyQueryFetcher(newOrderSearch);
  const toast = useToast();
  const navigate = useNavigate();
  const caseNumberInputRef = useRef<HTMLInputElement>(null);

  const [searchState, setSearchState] = useState<SearchTableProps['searchState']>({
    ...defaultSearchState,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchReqBody, setSearchReqBody] = useState<{
    input: SearchTableProps['searchState'];
    order?: SortOrder;
    sort?: string;
    limit: string;
    page: number;
  }>();

  const [searchData, setSearchData] = useState<SearchedOrderWrapper | null>(null);

  const limit = searchReqBody?.limit || '10';
  const page = searchReqBody?.page || 1;
  /**
   * function to handle change in search request body
   * @param params - new search request body
   */
  const onChangeSearchReqBody = useCallback((params: Partial<NonNullable<typeof searchReqBody>>) => {
    setSearchReqBody(prevState => {
      if (!prevState) return prevState;
      return {
        ...prevState,
        ...params,
      };
    });
  }, []);

  /**
   * function to handle change in limit of items to be displayed
   * @param limit - selected limit
   */
  const setLimit = useCallback(
    (limit: number) => {
      onChangeSearchReqBody({
        page: 1,
        limit: limit.toString(), // convert to string as the API expects a string
      });
    },
    [onChangeSearchReqBody]
  );

  /**
   * function to handle change in page number
   * @param page - selected page number
   */
  const setPage = useCallback(
    (page: number) => {
      onChangeSearchReqBody({
        page,
      });
    },
    [onChangeSearchReqBody]
  );

  /**
   * Sorts the filter results table by column either ascending or descending
   *
   * @param column - represents order by query in form of columnId-(asc|desc), eg:- patientName-desc
   */
  const handleSortByColumn = useCallback(
    (column: string) => {
      // the table JS sends us -column or column depending on sort order
      const { id: columnId, order } = getSortableOrder(column);
      onChangeSearchReqBody({
        page: 1,
        sort: columnId,
        order: order === 'asc' ? SortOrder.Asc : SortOrder.Desc, // Generally up arrow is asc, down arrow is desc
      });
    },
    [onChangeSearchReqBody]
  );

  /**
   *
   * @param e - event object
   * function to handle change in input values
   * @param name - name of the input field
   * @param value - value of the input field
   */
  const onInputSearchValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const trimmedValue = value.trim();
    setSearchState(prevState => ({
      ...prevState,
      [name]: trimmedValue,
    }));
  };
  /**
   * function to handle change in selected case statuses
   * @param selectedOptions - selected options from the dropdown
   */
  const onChangeCaseStatuses = (selectedOptions: DropdownOption[]) => {
    const selectedValues = selectedOptions.map(option => option.value) as OrderStatus[];
    setSearchState(prevState => ({
      ...prevState,
      statuses: selectedValues,
    }));
  };

  /**
   * function to handle reset of search input values
   */
  const onResetSearch = useCallback(() => {
    setSearchData(null);
    setSearchReqBody(undefined);
    setSearchState({
      ...defaultSearchState,
    });
    caseNumberInputRef.current?.focus();
  }, []);

  /**
   * function to handle submit of search form
   * @param e - event object
   */
  const onSubmitSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // set the search request body, this will trigger the useEffect to fetch data
    setSearchReqBody({
      input: searchState,
      order: undefined, // we are not sorting by default
      sort: undefined, // we are not sorting by default
      page: 1,
      limit,
    });
    AnalyticsService.track(AnalyticsEventName.SearchSubmitted, searchState);
  };

  /**
   * this effect is used to set the search state from the url params
   * and set the search request body if any params are present on page load
   * this is used to keep the search params in sync with the search state
   */
  useEffect(() => {
    const caseNumber = searchParams.get('caseNumber') || '';
    const patientFirstName = searchParams.get('patientFirstName') || '';
    const patientLastName = searchParams.get('patientLastName') || '';
    const providerName = searchParams.get('providerName') || '';
    const accountNumber = searchParams.get('accountNumber') || '';
    const statuses = searchParams.get('statuses') || '';
    const statusArray = statuses.split(',').filter((status): status is OrderStatus => !!status);

    const newState = {
      caseNumber,
      patientFirstName,
      patientLastName,
      providerName,
      accountNumber,
      statuses: statusArray,
    };
    setSearchState(newState);

    setSearchReqBody(prevState => {
      // if there is previous state, we will return it
      if (prevState) return prevState;
      // if there is no previous state, we will create a new state with the new search state
      return {
        input: newState,
        order: SortOrder.Desc,
        sort: 'labReceivedDate',
        limit: '10',
        page: 1,
      };
    });
  }, [searchParams]);

  /**
   * this effect is used to set the search params in the url
   * whenever the search request body changes
   * this is used to keep the search params in sync with the search state
   * and to allow the user to share the url with the search params
   */
  useEffect(() => {
    if (!searchReqBody?.input || !Object.keys(searchReqBody.input).length) {
      setSearchParams({}, { replace: true });
      return;
    }
    const input = searchReqBody.input;

    const params = new URLSearchParams();
    Object.keys(input).forEach(key => {
      const value = input[key as keyof typeof input];
      if (Array.isArray(value)) {
        if (value.length) {
          params.set(key, value.join(','));
        }
      } else if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    });
    setSearchParams(params, { replace: true });
  }, [searchReqBody?.input, setSearchParams]);

  /**
   * this effect is used to fetch the search results
   * whenever the search request body changes
   * this is used to trigger the search when the user submits the form
   * and to fetch the data from the server
   */
  useEffect(() => {
    if (!searchReqBody) return;
    const { input, order, sort, limit, page } = searchReqBody;

    const selectedStatuses = input.statuses
      .map(status => {
        const foundCaseStatus = CASE_STATUS_OPTIONS.find(option => option.id === status.toLowerCase());
        return foundCaseStatus ? foundCaseStatus.value : status;
      })
      .flat();

    const variables: NewOrderSearchQueryVariables = {
      input: { ...input, statuses: selectedStatuses, excludeStatuses: [OrderStatus.Pending] },
      limit: parseInt(limit),
      offset: (page - 1) * parseInt(limit),
    };

    if (order && sort) {
      variables.sort = {
        direction: order,
        field: sort,
      };
    }

    const onFetchSearch = async () => {
      try {
        const data = await orderSearchFetcher(variables);
        if (data.orders.length === 1 && page === 1) {
          navigate(`/orders/${data.orders[0].orderNumber}`);
        } else {
          setSearchData(data);
        }
      } catch (error) {
        setSearchData(null);
        toast.notify('Error fetching search results', ToastNotificationType.Error);
      }
    };
    onFetchSearch();
  }, [navigate, orderSearchFetcher, searchReqBody, toast]);

  /**
   * those are the options to be displayed in the case status dropdown
   */
  const caseStatusesOptions = useMemo<DropdownOption[]>(() => {
    const supportedStatuses = getSupportedCaseStatuses();
    return supportedStatuses.map(status => {
      return {
        label: CaseStatus[status as OrderStatus]?.displayName || capitalize(status),
        value: status,
      };
    });
  }, []);

  /**
   * those are the values to be displayed in the case status dropdown
   * the values are derived from the search state
   */
  const caseStatusesValues = useMemo(() => {
    return searchState.statuses
      .map(status => {
        const foundStatusOption = caseStatusesOptions.find(option => option.value === status);
        return foundStatusOption;
      })
      .filter((option): option is DropdownOption => !!option);
  }, [caseStatusesOptions, searchState.statuses]);

  const sortByColumn = convertSortableOrderToString({ id: searchReqBody?.sort, order: searchReqBody?.order });

  /**
   * show pagination only if search req body input is not empty as per the AC
   */
  const showPagination = useMemo(() => {
    // if searchReqBody is not set or loading, we don't show pagination
    if (loading || !searchReqBody?.input) return false;
    return !Object.values(searchReqBody.input).every(value => !value.toString());
  }, [searchReqBody?.input, loading]);

  return (
    <div className="flex gap-4 flex-col p-4 bg-white flex-grow">
      <form className="flex gap-2 items-start flex-wrap" onSubmit={onSubmitSearch}>
        <Input
          id="caseNumber"
          name="caseNumber"
          type="text"
          label="Case Number"
          placeholder="Scan barcode or type"
          data-qa="case-number"
          value={searchState.caseNumber}
          onChange={onInputSearchValue}
          autoFocus
          ref={caseNumberInputRef}
          containerClassName="flex-grow"
        />
        <Input
          id="patientFirstName"
          name="patientFirstName"
          type="text"
          label="Patient First Name"
          placeholder="Enter patient first name"
          data-qa="patient-first-name"
          value={searchState.patientFirstName}
          onChange={onInputSearchValue}
          containerClassName="flex-grow"
        />
        <Input
          id="patientLastName"
          name="patientLastName"
          type="text"
          label="Patient Last Name"
          placeholder="Enter patient last name"
          data-qa="patient-last-name"
          value={searchState.patientLastName}
          onChange={onInputSearchValue}
          containerClassName="flex-grow"
        />
        <Input
          id="providerName"
          name="providerName"
          type="text"
          label="Provider Name"
          placeholder="Enter provider name"
          data-qa="provider-name"
          value={searchState.providerName}
          onChange={onInputSearchValue}
          containerClassName="flex-grow"
        />
        <Input
          id="accountNumber"
          name="accountNumber"
          type="text"
          label="Account Number"
          placeholder="Enter account number"
          data-qa="account-number"
          value={searchState.accountNumber}
          onChange={onInputSearchValue}
          containerClassName="flex-grow"
        />
        <div className="flex-grow">
          <MultiSelectSearchableDropdown
            placeholder="All statuses"
            className="min-w-52"
            dropdownOptions={caseStatusesOptions}
            values={caseStatusesValues}
            onSelectedValueChange={onChangeCaseStatuses}
            dataQa="case-status"
            label="Case Status"
          />
        </div>
        <div className="flex gap-2 self-end h-[38px]">
          <Button type="reset" variant="bordered" onClick={onResetSearch}>
            Reset
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            Search
          </Button>
        </div>
      </form>
      <div className="flex gap-4">
        <div className="flex-grow overflow-x-auto">
          <SearchTable
            page={page}
            setPage={setPage}
            sortByColumn={sortByColumn}
            setSortByColumn={handleSortByColumn}
            limit={+limit}
            setLimit={setLimit}
            data={searchData}
            loading={loading}
            searchState={searchState}
            showPagination={showPagination}
          />
        </div>
      </div>
    </div>
  );
};
